import React from "react";

/**
 * TitleBlock - a professional, AutoCAD-style title strip rendered BELOW a
 * deliverable drawing (plan / section / elevation / foundation). It is a
 * self-contained HTML strip placed OUTSIDE the drawing SVG so it can never
 * overlap the plan geometry, legend, dimension chains or hatches.
 *
 * Fields: project name, sheet title, sheet number (auto per discipline),
 * scale, date, design-code reference, a north arrow, and a clear advisory
 * that the output must be reviewed/approved by a licensed engineer.
 */

// Map the working-model design_code to a human, code-aware reference string.
export function codeReference(designCode, isAr) {
  const c = String(designCode || "").toUpperCase();
  if (c.includes("ACI") || c === "ACI_318") return isAr ? "ACI 318 · IBC" : "ACI 318 · IBC";
  if (c.includes("EC2") || c.includes("EURO")) return "Eurocode 2 · EN 1992";
  // Default / Saudi
  return isAr ? "SBC 304 · SBC 201" : "SBC 304 · SBC 201";
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const Cell = ({ label, value, testid, grow = true }) => (
  <div
    className={`border-s border-slate-200 px-3 py-1.5 first:border-s-0 ${grow ? "flex-1" : ""} min-w-[86px]`}
    data-testid={testid}
  >
    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
    <div className="truncate text-[13px] font-extrabold leading-tight text-slate-900">{value || "—"}</div>
  </div>
);

const NorthArrow = () => (
  <div className="flex flex-col items-center justify-center border-s border-slate-200 px-3 py-1.5" data-testid="title-block-north">
    <svg width="20" height="26" viewBox="0 0 20 26" aria-hidden>
      <path d="M10 2 L15 20 L10 15 L5 20 Z" fill="#0F172A" />
    </svg>
    <div className="text-[10px] font-black text-slate-900">N</div>
  </div>
);

export default function TitleBlock({
  isAr = true,
  projectName = "",
  sheetTitle = "",
  sheetNo = "",
  scale = "1:100",
  designCode = "",
  showNorth = true,
  firm = null,
}) {
  const firmName = (firm?.firm_name || "").trim();
  const engineerName = (firm?.engineer_name || "").trim();
  const license = (firm?.license_no || "").trim();
  const logoB64 = firm?.logo_base64 || null;
  const logoSrc = logoB64 ? (logoB64.startsWith("data:") ? logoB64 : `data:image/png;base64,${logoB64}`) : null;

  return (
    <div
      dir={isAr ? "rtl" : "ltr"}
      data-testid="title-block"
      className="mt-3 overflow-hidden rounded-lg border-2 border-slate-800 bg-white text-slate-900 shadow-sm"
    >
      {/* header strip */}
      <div className="flex items-center justify-between bg-slate-900 px-3 py-1">
        <span className="flex items-center gap-2">
          {logoSrc && (
            <img src={logoSrc} alt="logo" data-testid="tb-logo" className="h-5 w-5 rounded-sm bg-white object-contain p-[1px]" />
          )}
          <span className="text-[11px] font-black uppercase tracking-wider text-white" data-testid="tb-firm">
            {firmName || (isAr ? "جدول بيانات اللوحة · TITLE BLOCK" : "TITLE BLOCK")}
          </span>
        </span>
        <span className="text-[10px] font-bold text-amber-400">AutoBIM Logic</span>
      </div>

      {/* data cells */}
      <div className="flex flex-wrap items-stretch">
        <Cell label={isAr ? "المشروع" : "Project"} value={projectName} testid="tb-project" />
        <Cell label={isAr ? "اسم اللوحة" : "Sheet title"} value={sheetTitle} testid="tb-sheet-title" />
        {engineerName && <Cell label={isAr ? "المهندس" : "Engineer"} value={engineerName} testid="tb-engineer" />}
        {license && <Cell label={isAr ? "رقم الترخيص" : "License"} value={license} testid="tb-license" grow={false} />}
        <Cell label={isAr ? "المقياس" : "Scale"} value={scale} testid="tb-scale" grow={false} />
        <Cell label={isAr ? "التاريخ" : "Date"} value={todayISO()} testid="tb-date" grow={false} />
        <Cell label={isAr ? "الكود المرجعي" : "Design code"} value={codeReference(designCode, isAr)} testid="tb-code" />
        <Cell label={isAr ? "رقم اللوحة" : "Sheet no."} value={sheetNo} testid="tb-sheet-no" grow={false} />
        {showNorth && <NorthArrow />}
      </div>

      {/* advisory */}
      <div className="border-t border-amber-300 bg-amber-50 px-3 py-1.5" data-testid="tb-advisory">
        <p className="text-[10.5px] font-semibold leading-snug text-amber-800">
          {isAr
            ? "مخرج استرشادي وفق مراجع الأكواد (SBC/IBC/ACI) وتسلسل بناء واقعي - يتطلب مراجعة واعتماد مهندس مرخّص قبل التنفيذ."
            : "Guidance output based on code references (SBC/IBC/ACI) and a realistic build sequence - requires review & approval by a licensed engineer before construction."}
        </p>
      </div>
    </div>
  );
}
